// src/components/security/browser.ts
/**
 * The two browser affordances the security page needs and this codebase does not already have:
 * putting a secret on the clipboard, and handing a text file to the user.
 *
 * Both exist because of the same moment: ten recovery codes are shown exactly once, and the admin
 * has to get them off the screen and into a password manager before they click "Hotovo". A copy
 * button that silently does nothing there is a lockout waiting for the next lost phone.
 */

/**
 * Copy `text`, and say whether it actually happened.
 *
 * The return value is the whole point. `navigator.clipboard.writeText` is unavailable outside a
 * secure context and can reject even inside one (Safari requires the call to be inside a user
 * gesture, and a denied permission rejects rather than resolving false), so a caller that assumes
 * success shows "Skopírované." over an empty clipboard. Every caller here renders a different
 * sentence on `false`, telling the reader to select the value and copy it by hand — which is why
 * `Mono` sets `user-select: all`.
 *
 * "Secure context" includes `http://localhost`, which is how this app is developed, but NOT the
 * `http://192.168.x.x` a phone on the same LAN would use to reach a dev server. The
 * `execCommand('copy')` fallback below covers that case and every browser old enough to lack the
 * async API; it is deprecated, not removed, and is still implemented everywhere this app runs.
 */
export async function copyText(text: string): Promise<boolean> {
    if (typeof text !== 'string' || !text) {
        return false;
    }
    try {
        if (
            typeof navigator !== 'undefined' &&
            navigator.clipboard &&
            typeof navigator.clipboard.writeText === 'function' &&
            typeof window !== 'undefined' &&
            window.isSecureContext
        ) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // Denied, or not in a user gesture. Fall through to the textarea — never report success,
        // and never log: the argument is a secret.
    }
    return legacyCopy(text);
}

/**
 * The pre-`navigator.clipboard` route: a real textarea, selected, copied, removed.
 *
 * It has to be a REAL element in the layout — `display: none`, `visibility: hidden` and a
 * zero-height box all make `select()` a no-op, so the node is one transparent pixel pinned to the
 * top-left instead. `position: fixed` keeps the page from scrolling to it. Focus is handed back to
 * whatever had it, because the callers on this page refocus a code input after every action and a
 * stolen focus would defeat that.
 */
function legacyCopy(text: string): boolean {
    if (typeof document === 'undefined' || !document.body) {
        return false;
    }
    const previous = document.activeElement;
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '0';
    area.style.width = '1px';
    area.style.height = '1px';
    area.style.padding = '0';
    area.style.border = 'none';
    area.style.outline = 'none';
    area.style.boxShadow = 'none';
    area.style.background = 'transparent';
    area.style.opacity = '0';
    document.body.appendChild(area);

    let copied = false;
    try {
        area.focus();
        area.select();
        if (typeof area.setSelectionRange === 'function') {
            area.setSelectionRange(0, text.length);
        }
        copied = document.execCommand('copy');
    } catch (e) {
        copied = false;
    }

    document.body.removeChild(area);
    if (previous && typeof (previous as HTMLElement).focus === 'function') {
        try {
            (previous as HTMLElement).focus();
        } catch (e) {
            /* the element may have been unmounted in the meantime; nothing to restore */
        }
    }
    return copied;
}

/**
 * Save `text` to disk as `filename`, via a transient object URL.
 *
 * A copy of `Invoicing.saveBlob`, kept local for the same reason as `ui.ts`: that function is a
 * private helper inside a page, not an exported utility, and the security page must not depend on
 * the invoicing page's internals. The `revokeObjectURL` delay is Invoicing's too — Safari has
 * historically cancelled the download if the URL is revoked in the same tick as the click.
 */
export function saveTextFile(filename: string, text: string): void {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
}
